/**
 * Hook for managing linkedTable initialization logic
 *
 * Handles the initialization of linkedTable state from loaded metadata,
 * including tracking initialization status.
 *
 * Note: The primary table name is now stored in tables[0].alias,
 * so no separate tableName prop is needed.
 */

import { useEffect, useRef } from 'react';

import { logger } from '@/lib/logger';
import type { LinkedTable } from '@/lib/types';

export interface UseLinkedTableInitializationProps {
  loadedLinkedTable: LinkedTable | null;
  isCreateMode: boolean;
  onInitializeLinkedTable: (linkedTable: LinkedTable) => void;
}

/**
 * Hook that manages linkedTable initialization from loaded metadata
 */
export const useLinkedTableInitialization = ({
  loadedLinkedTable,
  isCreateMode,
  onInitializeLinkedTable,
}: UseLinkedTableInitializationProps) => {
  const initializedLinkedTableIdRef = useRef<string | null>(null);

  // Initialize linkedTable state from loaded metadata (only once per linkedTable)
  useEffect(() => {
    if (
      loadedLinkedTable &&
      !isCreateMode &&
      loadedLinkedTable.id !== initializedLinkedTableIdRef.current
    ) {
      logger.debug('Initializing linkedTable state from loaded metadata', {
        linkedTableId: loadedLinkedTable.id,
        filters: loadedLinkedTable.filters?.length || 0,
      });
      initializedLinkedTableIdRef.current = loadedLinkedTable.id;
      onInitializeLinkedTable(loadedLinkedTable);
    }
  }, [loadedLinkedTable, isCreateMode, onInitializeLinkedTable]);

  return {
    initializedLinkedTableId: initializedLinkedTableIdRef.current,
  };
};

